import { supabase } from '../SupabaseClient/supabase-client';
import type { PortfolioData } from '@/types';
import type { SavedContributionRecord } from '../Contributions/contributions-service';
import { mapPortfolioResponse } from '../Portfolio/portfolio-service';
import { mapSavedContributionRow } from '../Contributions/contributions-service';

export type DashboardSummary = {
  totalAssets: number;
  totalInvested: number;
  profitLoss: number;
  profitability: number;
  estimatedTickers: string[];
};

export type DashboardTopAsset = {
  ticker: string;
  shortName: string;
  currentValue: number;
  changePercent: number;
};

export type DashboardData = {
  portfolio: PortfolioData;
  contributions: SavedContributionRecord[];
  summary: DashboardSummary;
  topAssets: DashboardTopAsset[];
};

const FUNCTION_NAME = import.meta.env.VITE_DASHBOARD_FUNCTION ?? 'dashboard';

const formatDateId = (value: string | number | Date, fallback = '') => {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return fallback;

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}${month}${day}`;
};

const normalizeContributionItem = (
  item: unknown,
  index: number,
): SavedContributionRecord | null => {
  // Try the existing mapper first (covers full SavedContributionRecord shape).
  const mapped = mapSavedContributionRow(item as Record<string, unknown>);
  if (mapped) {
    return mapped;
  }

  if (!item || typeof item !== 'object') return null;
  const value = item as Record<string, unknown>;
  const timestamp = value.timestamp ?? value.date ?? value.createdAt ?? value.created_at;
  const totalRaw =
    value.total ??
    value.totalDistributed ??
    value.amount ??
    value.value ??
    value.total_value ??
    value.totalValue;
  const total = typeof totalRaw === 'number' ? totalRaw : Number(totalRaw);
  const hasTotal = Number.isFinite(total);

  const date = timestamp ? new Date(timestamp as string) : null;
  const dateId = date ? formatDateId(date) : '';
  const fallbackId = `contrib-${index}`;

  return {
    id: dateId || fallbackId,
    createdAt: date ? date.toISOString() : undefined,
    amount: hasTotal ? total : 0,
    totalDistributed: hasTotal ? total : 0,
    totalQuantity: 0,
    totalCashRemainder: 0,
    assetClassesCount: undefined,
    eligibleAssetsCount: undefined,
    recommendations: [],
  };
};

const normalizeContributions = (input: unknown): SavedContributionRecord[] => {
  if (!input) return [];

  const payload = input as Record<string, unknown>;
  const candidates =
    (Array.isArray(payload.recentContributions) && payload.recentContributions) ||
    (Array.isArray(payload.contributions) && payload.contributions) ||
    (Array.isArray(payload.items) && payload.items) ||
    (Array.isArray(payload.data) && payload.data) ||
    (Array.isArray(payload) && payload) ||
    (Array.isArray(input) && input) ||
    [];

  return candidates.map(normalizeContributionItem).filter(Boolean) as SavedContributionRecord[];
};

const normalizePortfolio = (input: unknown): PortfolioData => {
  const payload = (input && typeof input === 'object' ? input : {}) as Record<string, unknown>;
  const portfolioSource =
    (payload.portfolio && typeof payload.portfolio === 'object' && payload.portfolio) ||
    (payload.portfolioData && typeof payload.portfolioData === 'object' && payload.portfolioData) ||
    input;

  return mapPortfolioResponse(portfolioSource);
};

export const dashboardService = {
  async getDashboard(): Promise<DashboardData> {
    const { data, error } = await supabase.functions.invoke(FUNCTION_NAME);
    if (error) {
      throw error;
    }

    const payload = (data && typeof data === 'object' ? data : {}) as Record<string, unknown>;
    const portfolio = normalizePortfolio(data);
    const contributions = normalizeContributions(
      (payload.contributions ?? payload.recentContributions) ?? data,
    );

    const rawSummary = (payload.summary && typeof payload.summary === 'object'
      ? payload.summary
      : {}) as Record<string, unknown>;

    const summary: DashboardSummary = {
      totalAssets: Number(rawSummary.totalAssets ?? 0),
      totalInvested: Number(rawSummary.totalInvested ?? 0),
      profitLoss: Number(rawSummary.profitLoss ?? 0),
      profitability: Number(rawSummary.profitability ?? 0),
      estimatedTickers: Array.isArray(rawSummary.estimatedTickers)
        ? (rawSummary.estimatedTickers as string[])
        : [],
    };

    const topAssets: DashboardTopAsset[] = Array.isArray(payload.topAssets)
      ? (payload.topAssets as DashboardTopAsset[])
      : [];

    return { portfolio, contributions, summary, topAssets };
  },
};
